import { Stack, StackProps } from '@mui/material';
import Box from '@mui/material/Box';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { useQcMode } from '@/hooks/use-qcMode';
import { ErrorType } from '@/types/QcInfo';
import AdditionalItems from './AdditionalItems';
import AdditionalPhotos from './AdditionalPhotos';
import AgentInternalComment from './AgentInternalComment';
import AgentPublicComment from './AgentPublicComment';
import CountryOrigin from './CountryOrigin';
import RemarkInfo from './RemarkInfo';
import WeightAndHallmark from './WeightAndHallmark';
import { Theme } from '../Theme';

const WrappedBox = ({ sx, children }: PropsWithChildren & StackProps) => {
  return (
    <Stack
      gap={1.5}
      border={`1px solid ${Theme.palette.grey[400]}`}
      borderRadius={1}
      padding="8px 1rem 1rem"
      position="relative"
      sx={{ ...sx }}
    >
      {children}
    </Stack>
  );
};

interface AdditionalInfoProps {
  showHallMarksAndWeight: boolean;
  isWeightHallmarkRequired: boolean;
  showWatchFunctioned: boolean;
  errors?: ErrorType[];
}

export default function AdditionalInfo({
  showHallMarksAndWeight,
  isWeightHallmarkRequired,
  showWatchFunctioned,
  errors,
}: AdditionalInfoProps) {
  const { t } = useTranslation();

  const { isViewMode } = useQcMode();

  return (
    <Stack gap={1}>
      <AdditionalItems isViewMode={isViewMode} />

      <RemarkInfo
        isViewMode={isViewMode}
        showWatchFunctioned={showWatchFunctioned}
        errors={errors}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'nowrap',
          padding: '1rem',
          pt: 0,
        }}
      >
        {showHallMarksAndWeight && (
          <WeightAndHallmark
            isRequired={isWeightHallmarkRequired}
            errors={errors}
          />
        )}

        <CountryOrigin errors={errors} />

        <WrappedBox
          sx={{
            mt: 0.75,
            background: (theme) => theme.palette.commentBoxColor.main,
          }}
        >
          <AdditionalPhotos
            title={t('QC_V2.MAIN.QC_INTERNAL_PHOTOS')}
            type="internal"
          />
          <AgentInternalComment />
        </WrappedBox>

        <WrappedBox
          sx={{ mt: 2, borderColor: (theme) => theme.palette.warning.main }}
        >
          <AdditionalPhotos
            title={t('QC_V2.MAIN.QC_PUBLIC_PHOTOS')}
            type="public"
          />
          <AgentPublicComment />
        </WrappedBox>
      </Box>
    </Stack>
  );
}
